import { useContext, useRef, useState, useEffect } from 'react';
import { getNudgeConfig } from '@/lib/nudges/registry';
import { NudgeGateContext } from './NudgeGateContext';

/**
 * Hook to acquire/release a nudge display slot.
 * Returns whether this nudge has an active slot (is allowed to display).
 */
export function useNudgeSlot(nudgeId: string, wantToShow: boolean): boolean {
  const context = useContext(NudgeGateContext);
  if (!context) {
    throw new Error('useNudgeSlot must be used within NudgeGateProvider');
  }

  const hadSlot = useRef(false);
  const [slotGranted, setSlotGranted] = useState(false);

  useEffect(() => {
    if (wantToShow) {
      // Try to acquire a slot
      const config = getNudgeConfig(nudgeId);
      const priority = config?.priority ?? 0;
      context.requestSlot(nudgeId, priority);
      hadSlot.current = true;
    } else {
      // Release any held slot
      if (hadSlot.current) {
        context.releaseSlot(nudgeId);
        hadSlot.current = false;
      }
    }
  }, [wantToShow, nudgeId, context]);

  // Subscribe to slot availability changes
  useEffect(() => {
    const hasSlot = context.hasSlot(nudgeId);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSlotGranted(hasSlot);
    // Re-check whenever active slots change
  }, [nudgeId, context]);

  return slotGranted && context.hasSlot(nudgeId);
}
